import { memo, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import { Flex, FlexCenter, IconButton } from '@elementary/common-ui/components';
import useIsOverflow from '@elementary/common-ui/hooks/useIsOverflow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Tooltip, Typography } from '@mui/material';
import { radius, space } from '@elementary/common-ui/style/utils';

const COLLAPSED_CHIP_LIMIT = 3;

const COLUMN_TYPES_ICON_MAP: Record<string, JSX.Element> = {
  timestamp: <AccessTimeIcon style={{ height: '16px', width: '16px' }} />,
};

const COLUMN_TYPES_LABEL_MAP: Record<string, string> = {
  string: 'String',
  numeric: 'Numeric',
  boolean: 'Boolean',
  timestamp: 'Timestamp',
};

const ArrowDownIconStyled = styled(ArrowDownIcon)<{ $flip: boolean }>`
  transform: ${({ $flip }) => ($flip ? 'rotate(-180deg)' : undefined)};
  transition: transform 0.1s;
  min-width: 10px;
`;

const ChipsWrapper = styled(Flex)<{ expanded: boolean }>`
  gap: ${space('sm')};
  overflow: hidden;
  flex-wrap: wrap;
  flex: 1;
  padding: ${space('xs')};
  height: ${({ expanded }) => (expanded ? 'auto' : '46px')};
  box-sizing: border-box;
`;

const IconChip = styled(FlexCenter)`
  background-color: ${({ theme }) => theme.palette.surface.hover};
  padding: ${space('xs')};
  border-radius: ${radius('sm')};
  gap: ${space('xs')};
  height: 32px;
`;

type Props = {
  columns: { name: string; type: keyof typeof COLUMN_TYPES_ICON_MAP }[];
};

const ColumnTagsExpanded = ({ columns }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<any>();
  const isOverflow = useIsOverflow(ref, false, [columns, expanded]);

  const hasMoreChips = columns?.length > COLLAPSED_CHIP_LIMIT;
  const visibleColumns = useMemo(
    () => (expanded ? columns : columns?.slice(0, COLLAPSED_CHIP_LIMIT)),
    [columns, expanded],
  );

  return (
    <Flex sx={{ paddingTop: 1 }}>
      <ChipsWrapper ref={ref} expanded={expanded}>
        {visibleColumns?.map((col, idx) => (
          <Tooltip
            key={`${col.name}-${idx}`}
            title={COLUMN_TYPES_LABEL_MAP[col.type]}
          >
            <IconChip>
              {col.type in COLUMN_TYPES_ICON_MAP && (
                <FlexCenter>{COLUMN_TYPES_ICON_MAP[col.type]}</FlexCenter>
              )}
              <Typography>{col.name}</Typography>
            </IconChip>
          </Tooltip>
        ))}
      </ChipsWrapper>
      {(isOverflow || expanded || hasMoreChips) && (
        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
          <IconButton
            sx={{
              mt: 0.5,
              padding: (theme) => `${theme.tokens.space.sm} !important`,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <ArrowDownIconStyled $flip={expanded} />
          </IconButton>
        </Tooltip>
      )}
    </Flex>
  );
};

export default memo(ColumnTagsExpanded);
